import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useCallback, useEffect, useMemo, useState } from "react";

import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import AlertBox from "components/Alert/alert";
import Loader from "components/Loader/loader";

import PanelCard from "layouts/dutyEstimator/components/PanelCard";
import SettingsPanel, { buildSettingsForm } from "layouts/dutyEstimator/components/SettingsPanel";
import DutyRatesPanel from "layouts/dutyEstimator/components/DutyRatesPanel";
import AgeBandsPanel from "layouts/dutyEstimator/components/AgeBandsPanel";
import FreightRatesPanel from "layouts/dutyEstimator/components/FreightRatesPanel";
import LevyOverridesPanel from "layouts/dutyEstimator/components/LevyOverridesPanel";
import PreviewPanel from "layouts/dutyEstimator/components/PreviewPanel";
import auditLogTableData from "layouts/dutyEstimator/data/auditLogTableData";
import { AUDIT_LOG_ENTITY_OPTIONS, VEHICLE_TYPE_OPTIONS, INPUT_SX } from "layouts/dutyEstimator/constants";

import {
  getDutyEstimatorConfig,
  updateDutyEstimatorSettings,
  createDutyRate,
  updateDutyRate,
  deleteDutyRate,
  createDutyBand,
  updateDutyBand,
  deleteDutyBand,
  previewDutyEstimate,
  getDutyEstimatorAuditLogs,
  getCategories,
} from "services/apiService";

const TABS = [
  "Settings",
  "Duty Rates",
  "Depreciation",
  "Over-age Penalties",
  "Freight Rates",
  "Levy Overrides",
  "Test Estimate",
  "Audit Log",
];

const EMPTY_ALERT = { open: false, type: "success", title: "", message: "", onConfirm: null };

function DutyEstimator() {
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [alert, setAlert] = useState(EMPTY_ALERT);

  const [settings, setSettings] = useState(buildSettingsForm({}));
  const [dutyRates, setDutyRates] = useState([]);
  const [depreciationBands, setDepreciationBands] = useState([]);
  const [penaltyBands, setPenaltyBands] = useState({});
  const [penaltyTab, setPenaltyTab] = useState(0);
  const [freightRates, setFreightRates] = useState([]);
  const [levyOverrides, setLevyOverrides] = useState([]);

  const [categories, setCategories] = useState([]);

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogPage, setAuditLogPage] = useState(1);
  const [auditLogEntityType, setAuditLogEntityType] = useState("");

  const showError = useCallback((message, title = "Oops!") => {
    setAlert({ open: true, type: "error", title, message, onConfirm: null });
  }, []);

  const showSuccess = useCallback((message, title = "Saved") => {
    setAlert({ open: true, type: "success", title, message, onConfirm: null });
  }, []);

  // The API returns Joi messages in `errors`, which name the exact field that was rejected.
  const describeFailure = (response, fallback) => {
    if (Array.isArray(response?.errors) && response.errors.length) {
      return response.errors.map((e) => e?.message || e?.msg || e).filter(Boolean).join(", ");
    }
    return response?.message || fallback;
  };

  // One call returns settings, duty rates and both age tables.
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDutyEstimatorConfig();
      const data = response?.data;
      if (data) {
        setSettings(buildSettingsForm(data.settings || {}));
        setDutyRates(data.dutyRates || []);
        setDepreciationBands(data.depreciationBands || []);
        setPenaltyBands(data.overAgePenaltyBands || {});
        setFreightRates(data.freightRates || []);
        setLevyOverrides(data.categoryLevyOverrides || []);
      } else if (response?.message) {
        showError(response.message, "Could not load configuration");
      }
    } catch (err) {
      console.error("Fetch duty estimator config failed:", err);
      showError("Failed to load the duty estimator configuration.");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await getCategories();
      const list = response?.data || response?.categories || (Array.isArray(response) ? response : []);
      setCategories((Array.isArray(list) ? list : []).filter((cat) => cat?.isDutyEstimatorAvailable && cat?.slug));
    } catch (err) {
      console.error("Fetch duty estimator categories failed:", err);
    }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDutyEstimatorAuditLogs({
        entityType: auditLogEntityType || undefined,
        page: auditLogPage,
        limit: 20,
      });
      if (response?.success) setAuditLogs(response?.data?.logs || []);
    } catch (err) {
      console.error("Fetch duty estimator audit logs failed:", err);
    } finally {
      setLoading(false);
    }
  }, [auditLogEntityType, auditLogPage]);

  useEffect(() => {
    fetchConfig();
    fetchCategories();
  }, [fetchConfig, fetchCategories]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleSettingsSave = async (payload) => {
    if (!Object.keys(payload).length) {
      showError("Nothing has been changed yet.", "No changes");
      return;
    }

    setLoading(true);
    try {
      const response = await updateDutyEstimatorSettings(payload);
      if (response?.success) {
        showSuccess(response?.message || "Duty estimator settings updated successfully.");
        fetchConfig();
      } else {
        showError(describeFailure(response, "Failed to update settings."));
      }
    } catch (err) {
      console.error("Update duty estimator settings failed:", err);
      showError("Failed to update settings.");
    } finally {
      setLoading(false);
    }
  };

  // Returns true when the write succeeded, so the panel knows whether to close its dialog.
  const runSave = useCallback(
    async (saveFn, noun) => {
      setLoading(true);
      try {
        const response = await saveFn();
        if (response?.success) {
          showSuccess(response?.message || `${noun} saved successfully.`);
          await fetchConfig();
          return true;
        }
        showError(describeFailure(response, `Failed to save the ${noun.toLowerCase()}.`));
        return false;
      } catch (err) {
        console.error(`Save ${noun} failed:`, err);
        showError(`Failed to save the ${noun.toLowerCase()}.`);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchConfig, showError, showSuccess]
  );

  const runDelete = useCallback(
    (deleteFn, noun) => {
      setAlert({
        open: true,
        type: "confirm",
        title: "Confirm Delete",
        message: `Are you sure you want to delete this ${noun.toLowerCase()}?`,
        onConfirm: async () => {
          setLoading(true);
          try {
            const response = await deleteFn();
            // A 204 returns no body, so anything without an explicit failure is a success.
            if (response?.success === false || response?.error) {
              showError(describeFailure(response, `Failed to delete the ${noun.toLowerCase()}.`));
            } else {
              showSuccess(`${noun} deleted successfully.`, "Deleted");
              await fetchConfig();
            }
          } catch (err) {
            console.error(`Delete ${noun} failed:`, err);
            showError(`Failed to delete the ${noun.toLowerCase()}.`);
          } finally {
            setLoading(false);
          }
        },
      });
    },
    [fetchConfig, showError, showSuccess]
  );

  const handleDutyRateSave = useCallback(
    (id, payload) => runSave(() => (id ? updateDutyRate(id, payload) : createDutyRate(payload)), "Duty rate"),
    [runSave]
  );
  const handleDutyRateDelete = useCallback((id) => runDelete(() => deleteDutyRate(id), "Duty rate"), [runDelete]);

  const handleDepreciationSave = useCallback(
    (id, payload) =>
      runSave(
        () => (id ? updateDutyBand("depreciationBands", id, payload) : createDutyBand("depreciationBands", payload)),
        "Depreciation band"
      ),
    [runSave]
  );
  const handleDepreciationDelete = useCallback(
    (id) => runDelete(() => deleteDutyBand("depreciationBands", id), "Depreciation band"),
    [runDelete]
  );

  const handlePenaltySave = useCallback(
    (id, payload) =>
      runSave(
        () => (id ? updateDutyBand("penaltyBands", id, payload) : createDutyBand("penaltyBands", payload)),
        "Penalty band"
      ),
    [runSave]
  );
  const handlePenaltyDelete = useCallback(
    (id) => runDelete(() => deleteDutyBand("penaltyBands", id), "Penalty band"),
    [runDelete]
  );

  const handleFreightRateSave = useCallback(
    (id, payload) =>
      runSave(
        () => (id ? updateDutyBand("freightRates", id, payload) : createDutyBand("freightRates", payload)),
        "Freight rate"
      ),
    [runSave]
  );
  const handleFreightRateDelete = useCallback(
    (id) => runDelete(() => deleteDutyBand("freightRates", id), "Freight rate"),
    [runDelete]
  );

  const handleLevyOverrideSave = useCallback(
    (id, payload) =>
      runSave(
        () => (id ? updateDutyBand("categoryLevyOverrides", id, payload) : createDutyBand("categoryLevyOverrides", payload)),
        "Levy override"
      ),
    [runSave]
  );
  const handleLevyOverrideDelete = useCallback(
    (id) => runDelete(() => deleteDutyBand("categoryLevyOverrides", id), "Levy override"),
    [runDelete]
  );

  const handlePreview = async (payload) => {
    setLoading(true);
    try {
      const response = await previewDutyEstimate(payload);
      if (response?.success) return response.data;
      showError(describeFailure(response, "Could not calculate an estimate."));
      return null;
    } catch (err) {
      console.error("Preview duty estimate failed:", err);
      showError("Could not calculate an estimate.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const dutyRateCategoryOptions = useMemo(
    () => categories.map((cat) => ({ value: String(cat.slug).toLowerCase(), label: cat.name })),
    [categories]
  );

  const previewCategoryOptions = useMemo(
    () => categories.map((cat) => ({ id: cat.id, label: cat.name })),
    [categories]
  );

  const activePenaltyType = VEHICLE_TYPE_OPTIONS[penaltyTab];
  const activePenaltyBands = penaltyBands?.[activePenaltyType.value] || [];

  const { columns: auditLogColumns, rows: auditLogRows } = useMemo(
    () => auditLogTableData({ logList: auditLogs }),
    [auditLogs]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loader />}

      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => {
          const action = alert.onConfirm;
          setAlert({ ...alert, open: false });
          if (action) action();
        }}
      />

      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox px={2}>
              <Tabs
                value={tab}
                onChange={(event, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                {TABS.map((label) => (
                  <Tab key={label} label={label} />
                ))}
              </Tabs>
            </MDBox>
          </Card>
        </MDBox>

        <Grid container spacing={6}>
          {tab === 0 && (
            <Grid item xs={12}>
              <SettingsPanel settings={settings} onChange={setSettings} onSave={handleSettingsSave} />
            </Grid>
          )}

          {tab === 1 && (
            <Grid item xs={12}>
              <DutyRatesPanel
                dutyRates={dutyRates}
                categoryOptions={dutyRateCategoryOptions}
                onSave={handleDutyRateSave}
                onDelete={handleDutyRateDelete}
                onValidationError={showError}
              />
            </Grid>
          )}

          {tab === 2 && (
            <Grid item xs={12}>
              <AgeBandsPanel
                title="Depreciation Table"
                caption="Applied to a used vehicle's HDV baseline before freight and insurance are added. New vehicles are never depreciated."
                bands={depreciationBands}
                rateKey="depreciationRate"
                rateLabel="Depreciation (%)"
                onSave={handleDepreciationSave}
                onDelete={handleDepreciationDelete}
                onValidationError={showError}
              />
            </Grid>
          )}

          {tab === 3 && (
            <Grid item xs={12}>
              <AgeBandsPanel
                title="Over-age Penalty Tables"
                caption="Charged on the Customs Value of a used vehicle once it passes the age threshold for its type."
                bands={activePenaltyBands}
                rateKey="penaltyRate"
                rateLabel="Penalty (%)"
                extraPayload={{ vehicleType: activePenaltyType.value }}
                header={
                  <MDBox px={2} pt={2}>
                    <Tabs
                      value={penaltyTab}
                      onChange={(event, value) => setPenaltyTab(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                    >
                      {VEHICLE_TYPE_OPTIONS.map((option) => (
                        <Tab key={option.value} label={option.label} />
                      ))}
                    </Tabs>
                  </MDBox>
                }
                onSave={handlePenaltySave}
                onDelete={handlePenaltyDelete}
                onValidationError={showError}
              />
            </Grid>
          )}

          {tab === 4 && (
            <Grid item xs={12}>
              <FreightRatesPanel
                freightRates={freightRates}
                onSave={handleFreightRateSave}
                onDelete={handleFreightRateDelete}
                onValidationError={showError}
              />
            </Grid>
          )}

          {tab === 5 && (
            <Grid item xs={12}>
              <LevyOverridesPanel
                overrides={levyOverrides}
                categoryOptions={previewCategoryOptions}
                onSave={handleLevyOverrideSave}
                onDelete={handleLevyOverrideDelete}
                onValidationError={showError}
              />
            </Grid>
          )}

          {tab === 6 && (
            <Grid item xs={12}>
              <PreviewPanel
                categoryOptions={previewCategoryOptions}
                onPreview={handlePreview}
                onValidationError={showError}
              />
            </Grid>
          )}

          {tab === 7 && (
            <Grid item xs={12}>
              <PanelCard
                title="Audit Log"
                caption="Every change to the estimator configuration, with the value before and after."
                action={
                  <TextField
                    select
                    label="Entity Type"
                    value={auditLogEntityType}
                    onChange={(e) => {
                      setAuditLogPage(1);
                      setAuditLogEntityType(e.target.value);
                    }}
                    SelectProps={{ IconComponent: ArrowDropDownIcon }}
                    sx={{ backgroundColor: "#fff", borderRadius: 1, minWidth: 220, ...INPUT_SX }}
                  >
                    {AUDIT_LOG_ENTITY_OPTIONS.map((option) => (
                      <MenuItem key={option.value || "all"} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                }
              >
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns: auditLogColumns, rows: auditLogRows }}
                    isSorted={false}
                    entriesPerPage={{ defaultValue: 20, entries: [10, 20, 35, 50] }}
                    showTotalEntries
                    noEndBorder
                    canSearch={false}
                    page={auditLogPage}
                    onPageChange={setAuditLogPage}
                  />
                </MDBox>
              </PanelCard>
            </Grid>
          )}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default DutyEstimator;
